import { Component, ComponentRef, Input, OnChanges, SimpleChanges, ViewChild, ViewContainerRef } from "@angular/core";
import { Item, ItemMetadata } from "@common/ADAPT.Common.Model/organisation/item";
import { Link } from "@common/ADAPT.Common.Model/organisation/link";
import { MeetingAgendaItem } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item";
import { MeetingNote, MeetingNoteType, MeetingNoteTypeMetadata } from "@common/ADAPT.Common.Model/organisation/meeting-note";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { FunctionUtilities } from "@common/lib/utilities/function-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { KanbanUiService } from "@org-common/lib/kanban/kanban-ui.service";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { debounceTime, filter, switchMap, tap } from "rxjs/operators";
import { KanbanService } from "../../kanban/kanban.service";
import { IMeetingAgendaItemComponent, MeetingAgendaItemComponentRegistry } from "../meeting-agenda-component-registry";
import { MeetingsService } from "../meetings.service";
import { MeetingsUiService } from "../meetings-ui.service";

@Component({
    selector: "adapt-meeting-sidebar-agenda-item",
    templateUrl: "./meeting-sidebar-agenda-item.component.html",
    styleUrl: "./meeting-sidebar-agenda-item.component.css",
    standalone: false,
})
export class MeetingSidebarAgendaItemComponent extends BaseComponent implements OnChanges {
    public readonly MeetingNoteIconClass = MeetingNoteTypeMetadata.IconClass;
    public readonly MeetingNoteType = MeetingNoteType;
    public readonly MeetingItemIconClass = ItemMetadata.IconClass;

    @Input() public agendaItem!: MeetingAgendaItem;

    @ViewChild("container", { read: ViewContainerRef }) public container!: ViewContainerRef;
    public agendaActionLinks: Link[] = [];
    public agendaObjectiveLinks: Link[] = [];
    public agendaNotes: MeetingNote[] = [];
    public canEditMeetingItem$: Observable<boolean>;
    public canEditAgendaItem = false;
    public hasEditableBoard = false;

    private triggerUpdate$ = new BehaviorSubject<void>(undefined);
    private currentCustomComponent?: ComponentRef<IMeetingAgendaItemComponent>;

    constructor(
        private meetingsService: MeetingsService,
        private meetingsUiService: MeetingsUiService,
        private kanbanService: KanbanService,
        private kanbanUiService: KanbanUiService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        this.triggerUpdate$.pipe(
            debounceTime(10),
            // agenda is valid with valid meeting nav prop - can possibly be detached/deleted???
            filter(() => !!this.agendaItem?.meeting),
            tap(() => this.checkLoadComponentSelectorComponent()),
            switchMap(() => forkJoin([
                this.meetingsService.getActionLinksForAgendaItem(this.agendaItem),
                this.meetingsService.getMeetingNotesForAgendaItem(this.agendaItem),
                this.meetingsService.getObjectiveLinksForAgendaItem(this.agendaItem),
                this.meetingsService.canEditMeetingItemsAsParticipant(this.agendaItem.meeting!),
            ])),
            this.takeUntilDestroyed(),
        ).subscribe(([agendaActionLinks, agendaNotes, agendaObjectiveLinks, canEditAgendaItem]) => {
            this.agendaActionLinks = agendaActionLinks;
            this.agendaObjectiveLinks = agendaObjectiveLinks;
            this.agendaNotes = agendaNotes;
            this.canEditAgendaItem = canEditAgendaItem;
        });

        this.triggerUpdate$.pipe(
            switchMap(() => this.kanbanService.hasAnyEditableBoard()),
            this.takeUntilDestroyed(),
        ).subscribe((hasEditableBoard) => this.hasEditableBoard = hasEditableBoard);

        this.canEditMeetingItem$ = this.triggerUpdate$.pipe(
            // agenda is valid with valid meeting nav prop - can possibly be detached/deleted???
            filter(() => !!this.agendaItem?.meeting),
            // needs to be able to create meeting item for team - not just editing any public board.
            // viewers not excluded for this one, they should be able to progress the meeting still
            switchMap(() => this.meetingsService.canEditMeetingItemsAsParticipant(this.agendaItem.meeting!, false)),
            this.takeUntilDestroyed(),
        );

        rxjsBreezeService.entityTypeChanged(Item).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((item) => {
            if (this.agendaActionLinks.find((a) => item.itemId === a.itemId)) {
                this.triggerUpdate$.next();
            }
        });

        rxjsBreezeService.entityTypeChanged(Objective).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((objective) => {
            if (this.agendaObjectiveLinks.find((l) => objective.objectiveId === l.objective1Id)) {
                this.triggerUpdate$.next();
            }
        });

        rxjsBreezeService.entityTypeChanged(Link).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((meetingItem) => {
            if (meetingItem.meetingAgendaItemId === this.agendaItem.meetingAgendaItemId) {
                this.triggerUpdate$.next();
            }
        });

        rxjsBreezeService.entityTypeChanged(MeetingNote).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((note) => {
            if (note.meetingAgendaItemId === this.agendaItem.meetingAgendaItemId) {
                this.triggerUpdate$.next();
            }
        });
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.agendaItem && this.agendaItem) {
            this.triggerUpdate$.next();
        }
    }

    public addDecision() {
        return this.addMeetingNoteOfType(MeetingNoteType.Decision).subscribe();
    }

    public addMinute() {
        return this.addMeetingNoteOfType(MeetingNoteType.Minute).subscribe();
    }

    public addMeetingItem() {
        return this.kanbanUiService.createActionForAgendaItem(this.agendaItem).subscribe();
    }

    private addMeetingNoteOfType(type: MeetingNoteType) {
        return this.meetingsUiService.createMeetingNoteForAgendaItem(this.agendaItem, type).pipe(
            tap(() => this.triggerUpdate$.next()),
        );
    }

    private checkLoadComponentSelectorComponent() {
        if (this.currentCustomComponent) {
            this.currentCustomComponent = undefined;
        }

        if (this.agendaItem?.componentSelector && this.container) {
            const type = MeetingAgendaItemComponentRegistry.get(this.agendaItem.componentSelector);
            if (!type) {
                throw new Error(`component is not registered - ${this.agendaItem.componentSelector} - needs to use the MeetingAgendaComponent class decorator`);
            }

            this.container.clear();
            this.currentCustomComponent = this.container.createComponent<IMeetingAgendaItemComponent>(type);
            const instance = this.currentCustomComponent.instance;
            instance.meetingAgendaItem = this.agendaItem;

            if (FunctionUtilities.isFunction(instance.onDataChanged)) {
                instance.onDataChanged!();
            }
        }
    }
}
